import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { LinkContainer } from 'react-router-bootstrap'
import { Alert, Container, Nav, Navbar, NavDropdown } from 'react-bootstrap'
import { logout, getUserDetails } from '../actions/userActions'

function Header() {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [navExpanded, setNavExpanded] = useState(false)

  const userLogin = useSelector(state => state.userLogin)
  const { userInfo } = userLogin

  const userDetails = useSelector(state => state.userDetails)
  const { user } = userDetails

  useEffect(() => {
    if (userInfo && (!user || !user._id)) {
      dispatch(getUserDetails('profile'))
    }
  }, [userInfo, user, dispatch])

  const logoutHandler = () => {
    dispatch(logout())
    setNavExpanded(false)
  }

  const handleNavClick = (path) => {
    setNavExpanded(false)
    navigate(path)
  }

  const showOnboardingAlert = userInfo && user && user._id && user.profile_complete === false

  const navLinkStyle = {
    fontWeight: '700',
    color: '#1a1a1a',
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  }

  return (
    <header>
      <Navbar
        expand="lg"
        expanded={navExpanded}
        onToggle={(val) => setNavExpanded(val)}
        className="bnb-navbar"
      >
        <Container fluid className="px-4">

          {/* ── Logo ── */}
          <LinkContainer to="/" onClick={() => setNavExpanded(false)}>
            <Navbar.Brand className="bnb-brand">
              <span className="bnb-brand-primary">BNB</span>
              <span className="bnb-brand-accent"> GOLF</span>
            </Navbar.Brand>
          </LinkContainer>

          <Navbar.Toggle aria-controls="bnbNav" />

          <Navbar.Collapse id="bnbNav">

            {/* ── Center links ── */}
            <Nav className="me-auto ms-3">
              {[
                { label: 'Leaderboard', path: '/leaderboard' },
                { label: 'League',      path: '/league' },
                { label: 'Events',      path: '/events' },
                { label: 'Players',     path: '/players' },
              ].map(({ label, path }) => (
                <Nav.Link
                  key={path}
                  className="bnb-nav-link"
                  style={navLinkStyle}
                  onClick={() => handleNavClick(path)}
                >
                  {label}
                </Nav.Link>
              ))}
            </Nav>

            {/* ── Right: user dropdown ── */}
            <Nav className="align-items-center">
              {userInfo ? (
                <NavDropdown
                  title={
                    <span className="bnb-user-title">
                      {user?.image ? (
                        <img
                          src={user.image}
                          alt={userInfo.name}
                          className="bnb-avatar-sm"
                        />
                      ) : (
                        <i className="fa-solid fa-circle-user bnb-avatar-icon"></i>
                      )}
                      <span className="bnb-user-name">{userInfo.name}</span>
                    </span>
                  }
                  id="userDropdown"
                  align="end"
                >
                  {/* ── Account ── */}
                  <NavDropdown.Item onClick={() => handleNavClick('/profile')}>
                    <i className="fa-solid fa-user me-2 text-muted" style={{ fontSize: '0.8rem' }}></i>
                    My Profile
                  </NavDropdown.Item>

                  {/* ── Admin section (only for admins) ── */}
                  {userInfo.isAdmin && (
                    <>
                      <NavDropdown.Divider />
                      <NavDropdown.Header style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--bnb-gold)', fontWeight: '800' }}>
                        Administration
                      </NavDropdown.Header>
                      <NavDropdown.Item onClick={() => handleNavClick('/admin/dashboard')}>
                        <i className="fa-solid fa-gauge me-2 text-muted" style={{ fontSize: '0.8rem' }}></i>
                        Dashboard
                      </NavDropdown.Item>
                      <NavDropdown.Item onClick={() => handleNavClick('/admin/userList')}>
                        <i className="fa-solid fa-users me-2 text-muted" style={{ fontSize: '0.8rem' }}></i>
                        Users
                      </NavDropdown.Item>
                      <NavDropdown.Item onClick={() => handleNavClick('/admin/tournaments')}>
                        <i className="fa-solid fa-trophy me-2 text-muted" style={{ fontSize: '0.8rem' }}></i>
                        Tournaments
                      </NavDropdown.Item>
                      <NavDropdown.Item onClick={() => handleNavClick('/admin/leagues')}>
                        <i className="fa-solid fa-ranking-star me-2 text-muted" style={{ fontSize: '0.8rem' }}></i>
                        Leagues
                      </NavDropdown.Item>
                      <NavDropdown.Item onClick={() => handleNavClick('/admin/courses')}>
                        <i className="fa-solid fa-flag me-2 text-muted" style={{ fontSize: '0.8rem' }}></i>
                        Courses
                      </NavDropdown.Item>
                    </>
                  )}

                  {/* ── Journalist section ── */}
                  {(userInfo.isAdmin || userInfo.roles?.includes('journalist')) && (
                    <>
                      <NavDropdown.Divider />
                      <NavDropdown.Header style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--bnb-gold)', fontWeight: '800' }}>
                        Journalist
                      </NavDropdown.Header>
                      <NavDropdown.Item onClick={() => handleNavClick('/news')}>
                        <i className="fa-solid fa-newspaper me-2 text-muted" style={{ fontSize: '0.8rem' }}></i>
                        View Blogs
                      </NavDropdown.Item>
                      <NavDropdown.Item onClick={() => handleNavClick('/news/create')}>
                        <i className="fa-solid fa-pen-to-square me-2 text-muted" style={{ fontSize: '0.8rem' }}></i>
                        Create Blog
                      </NavDropdown.Item>
                      <NavDropdown.Item onClick={() => handleNavClick('/admin/leagues')}>
                        <i className="fa-solid fa-ranking-star me-2 text-muted" style={{ fontSize: '0.8rem' }}></i>
                        Leagues
                      </NavDropdown.Item>
                    </>
                  )}

                  <NavDropdown.Divider />
                  <NavDropdown.Item onClick={logoutHandler}>
                    <i className="fa-solid fa-right-from-bracket me-2 text-muted" style={{ fontSize: '0.8rem' }}></i>
                    Logout
                  </NavDropdown.Item>
                </NavDropdown>
              ) : (
                <LinkContainer to="/login">
                  <Nav.Link className="bnb-login-btn" onClick={() => setNavExpanded(false)}>
                    <i className="fa-solid fa-user me-1"></i> Login
                  </Nav.Link>
                </LinkContainer>
              )}
            </Nav>

          </Navbar.Collapse>
        </Container>
      </Navbar>

      {showOnboardingAlert && (
        <Alert variant='warning' className='mb-0 rounded-0 text-center py-2' style={{ fontSize: '0.875rem' }}>
          <i className="fa-solid fa-circle-exclamation me-2"></i>
          Your profile is incomplete —{' '}
          <LinkContainer to="/profile" style={{ display: 'inline' }}>
            <Alert.Link>complete your account</Alert.Link>
          </LinkContainer>{' '}
          to set your name and handicap.
        </Alert>
      )}
    </header>
  )
}

export default Header
